import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { KeywordSuggestionTable, TargetTable } from "./KeywordTables";
import { NegativeKeywordTargeting } from "./NegativeKeywordTargeting";
import { CompactEmptyState } from "./SectionLayout";
import { currencyLabel, keywordKey, moneyStep } from "./sponsored-brands-format";
import {
  type KeywordRow,
  MATCH_TYPES,
  type MatchType,
  type NegativeKeywordRow,
  type SponsoredBrandsMoneyCapabilities,
} from "./sponsored-brands-model";

export function KeywordTargeting({
  money,
  activeTab,
  onTabChange,
  input,
  onInputChange,
  rows,
  onRowsChange,
  matchTypes,
  onMatchTypesChange,
  bid,
  onBidChange,
  onAdd,
  negativeInput,
  onNegativeInputChange,
  negativeKeywords,
  onNegativeKeywordsChange,
  addResult,
  suggestions,
  onAddSuggestions,
  suggestionsLoading,
}: {
  money: SponsoredBrandsMoneyCapabilities;
  activeTab: string;
  onTabChange: (value: string) => void;
  input: string;
  onInputChange: (value: string) => void;
  rows: KeywordRow[];
  onRowsChange: (rows: KeywordRow[]) => void;
  matchTypes: Record<MatchType, boolean>;
  onMatchTypesChange: (value: Record<MatchType, boolean>) => void;
  bid: string;
  onBidChange: (value: string) => void;
  onAdd: () => void;
  negativeInput: string;
  onNegativeInputChange: (value: string) => void;
  negativeKeywords: NegativeKeywordRow[];
  onNegativeKeywordsChange: (value: NegativeKeywordRow[]) => void;
  addResult: { added: number; attempted: number } | null;
  suggestions: KeywordRow[];
  onAddSuggestions: (rows: KeywordRow[]) => void;
  suggestionsLoading: boolean;
}) {
  const { t } = useTranslation("campaignCreation");
  const [bidMode, setBidMode] = useState<"suggested" | "custom">("suggested");
  const visibleSuggestions = suggestions.filter((row) => matchTypes[row.matchType]);
  const selectedSuggestionKeys = new Set(rows.map((row) => keywordKey(row.matchType, row.keyword)));
  const allVisibleSuggestionsSelected =
    visibleSuggestions.length > 0 &&
    visibleSuggestions.every((row) =>
      selectedSuggestionKeys.has(keywordKey(row.matchType, row.keyword)),
    );

  const toggleSuggestions = (nextRows: KeywordRow[], selected: boolean) => {
    const keys = new Set(nextRows.map((row) => keywordKey(row.matchType, row.keyword)));
    if (!selected) {
      onRowsChange(rows.filter((row) => !keys.has(keywordKey(row.matchType, row.keyword))));
      return;
    }
    onAddSuggestions(
      nextRows.map((row) => ({
        ...row,
        bid,
      })),
    );
  };
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-3 font-semibold">{t("sb.targeting.keyword")}</h3>
        <div
          data-slot="sb-keyword-workspace"
          className="grid h-[500px] max-h-[500px] grid-cols-2 overflow-hidden rounded-lg border border-border"
        >
          <Tabs value={activeTab} onValueChange={onTabChange} className="min-h-0 min-w-0 gap-0 p-4">
            <TabsList className="shrink-0 self-start">
              <TabsTrigger value="suggestions">{t("sb.keyword.suggestions")}</TabsTrigger>
              <TabsTrigger value="enter">{t("sb.keyword.enter")}</TabsTrigger>
              <TabsTrigger value="library" disabled title={t("sb.blockers.keywordLibrary")}>
                {t("sb.keyword.library")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="suggestions" className="flex min-h-0 flex-col gap-3 pt-4">
              <KeywordBidControls
                money={money}
                bidMode={bidMode}
                onBidModeChange={setBidMode}
                bid={bid}
                onBidChange={onBidChange}
                matchTypes={matchTypes}
                onMatchTypesChange={onMatchTypesChange}
              />
              <div className="min-h-0 flex-1">
                {suggestionsLoading ? (
                  <p className="text-sm text-muted-foreground">{t("sb.products.checking")}</p>
                ) : null}
                <Tabs defaultValue="keywords" className="h-full min-h-0 gap-0">
                  <TabsList className="shrink-0 self-start">
                    <TabsTrigger value="keywords">{t("sb.keyword.keywords")}</TabsTrigger>
                    <TabsTrigger value="groups">{t("sb.keyword.targetingGroups")}</TabsTrigger>
                  </TabsList>
                  <TabsContent value="keywords" className="min-h-0 pt-3">
                    <ScrollArea scrollFade scrollbarGutter className="h-full">
                      <KeywordSuggestionTable
                        money={money}
                        rows={visibleSuggestions}
                        selectedKeys={selectedSuggestionKeys}
                        allSelected={allVisibleSuggestionsSelected}
                        onToggle={(row, selected) => toggleSuggestions([row], selected)}
                        onToggleAll={(selected) => toggleSuggestions(visibleSuggestions, selected)}
                      />
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent value="groups" className="min-h-0 pt-3">
                    <ScrollArea scrollFade scrollbarGutter className="h-full">
                      <Table className="min-w-[430px]">
                        <TableHeader className="sticky top-0 bg-background">
                          <TableRow>
                            <TableHead>{t("sb.keyword.targetingGroups")}</TableHead>
                            <TableHead>{t("sb.keyword.matchTypeColumn")}</TableHead>
                            <TableHead>{t("sb.keyword.suggestedBid")}</TableHead>
                            <TableHead className="w-10" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={4}>
                              <CompactEmptyState />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </div>
            </TabsContent>
            <TabsContent value="library" className="pt-4">
              <p className="text-sm text-muted-foreground">{t("sb.keyword.libraryEmpty")}</p>
            </TabsContent>
            <TabsContent value="enter" className="flex min-h-0 flex-col gap-3 pt-4">
              <KeywordBidControls
                money={money}
                bidMode={bidMode}
                onBidModeChange={setBidMode}
                bid={bid}
                onBidChange={onBidChange}
                matchTypes={matchTypes}
                onMatchTypesChange={onMatchTypesChange}
              />
              <Field className="min-h-0 flex-1">
                <FieldLabel className="sr-only">{t("sb.keyword.list")}</FieldLabel>
                <Textarea
                  className="min-h-0 flex-1 resize-none"
                  aria-label={t("sb.keyword.list")}
                  value={input}
                  onChange={(event) => onInputChange(event.target.value)}
                  placeholder={t("sb.keyword.placeholder")}
                />
                <FieldDescription>{t("sb.keyword.maximum")}</FieldDescription>
              </Field>
              <Button
                type="button"
                className="self-end"
                disabled={!input.trim() || !MATCH_TYPES.some((type) => matchTypes[type])}
                onClick={onAdd}
              >
                {t("sb.actions.add")}
              </Button>
              {addResult ? (
                <div className="grid shrink-0 gap-1">
                  <Alert data-slot="sb-keyword-add-success" variant="success" className="py-2">
                    <AlertDescription>
                      {addResult.added} / {addResult.attempted} {t("sb.keyword.addSuccess")}
                    </AlertDescription>
                  </Alert>
                  <Alert data-slot="sb-keyword-add-failure" variant="error" className="py-2">
                    <AlertDescription>
                      {addResult.attempted - addResult.added} / {addResult.attempted}{" "}
                      {t("sb.keyword.addFailure")}
                    </AlertDescription>
                  </Alert>
                </div>
              ) : null}
            </TabsContent>
          </Tabs>
          <TargetTable money={money} rows={rows} onRowsChange={onRowsChange} />
        </div>
      </div>
      <NegativeKeywordTargeting
        negativeInput={negativeInput}
        onNegativeInputChange={onNegativeInputChange}
        negativeKeywords={negativeKeywords}
        onNegativeKeywordsChange={onNegativeKeywordsChange}
      />
    </div>
  );
}

function KeywordBidControls({
  money,
  bidMode,
  onBidModeChange,
  bid,
  onBidChange,
  matchTypes,
  onMatchTypesChange,
}: {
  money: SponsoredBrandsMoneyCapabilities;
  bidMode: "suggested" | "custom";
  onBidModeChange: (value: "suggested" | "custom") => void;
  bid: string;
  onBidChange: (value: string) => void;
  matchTypes: Record<MatchType, boolean>;
  onMatchTypesChange: (value: Record<MatchType, boolean>) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const bidOptions = [
    { value: "suggested", label: t("sb.keyword.suggestedBid") },
    { value: "custom", label: t("sb.keyword.customBid") },
  ];
  return (
    <FieldGroup data-slot="sb-keyword-bid-controls" className="shrink-0 gap-3">
      <Field>
        <FieldLabel>{t("sb.keyword.bid")}</FieldLabel>
        <div data-slot="sb-keyword-bid-inputs" className="flex w-full gap-3">
          <Select
            items={bidOptions}
            value={bidMode}
            onValueChange={(value) => {
              if (value === "suggested" || value === "custom") onBidModeChange(value);
            }}
          >
            <SelectTrigger className="w-44 shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {bidOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          {bidMode === "custom" ? (
            <InputGroup className="min-w-0 flex-1">
              <InputGroupAddon>
                <InputGroupText>{currencyLabel(money.currencyCode)}</InputGroupText>
              </InputGroupAddon>
              <InputGroupInput
                aria-label={t("sb.keyword.customBid")}
                type="number"
                min={money.bid.min}
                max={money.bid.max}
                step={moneyStep(money.bid.decimals)}
                value={bid}
                onChange={(event) => onBidChange(event.target.value)}
              />
            </InputGroup>
          ) : null}
        </div>
      </Field>
      <FieldSet className="gap-0">
        <FieldLegend variant="label" className="sr-only">
          {t("sb.keyword.matchType")}
        </FieldLegend>
        <div
          data-slot="sb-keyword-match-row"
          className="flex flex-row items-center justify-between gap-4"
        >
          <span className="shrink-0 text-sm font-medium">{t("sb.keyword.matchType")}</span>
          <div data-slot="sb-keyword-match-types" className="ml-auto flex flex-nowrap gap-4">
            {MATCH_TYPES.map((matchType) => {
              const label = t(`sb.keyword.match.${matchType}`);
              return (
                <Field
                  key={matchType}
                  orientation="horizontal"
                  className="shrink-0 flex-nowrap items-center gap-1.5"
                >
                  <Badge
                    variant={matchType === "B" ? "info" : matchType === "P" ? "neutral" : "warning"}
                    className="size-5 shrink-0 p-0"
                  >
                    {matchType}
                  </Badge>
                  <Checkbox
                    checked={matchTypes[matchType]}
                    onCheckedChange={(checked) =>
                      onMatchTypesChange({ ...matchTypes, [matchType]: checked === true })
                    }
                    aria-label={`${matchType} ${label}`}
                  />
                  <FieldLabel className="whitespace-nowrap">{label}</FieldLabel>
                </Field>
              );
            })}
          </div>
        </div>
      </FieldSet>
    </FieldGroup>
  );
}
